import { onMounted, ref, Ref, watch } from 'vue';
import Message from '@/utils/message';

export interface MessageType {
  GET_DATA_IF_FAILED?: string;
  GET_DATA_IF_SUCCEED?: string;
  EXPORT_DATA_IF_FAILED?: string;
  EXPORT_DATA_IF_SUCCEED?: string;
}
export interface OptionsType {
  requestError?: () => void;
  requestSuccess?: () => void;
  exportError?: () => void;
  exportSuccess?: () => void;
  pagination?: boolean;
  message?: MessageType;
  preRequest?: () => void;
  immediate?: boolean;
  defaultPageSize?: number;
}

const DEFAULT_MESSAGE: MessageType = {
  GET_DATA_IF_FAILED: '获取列表数据失败',
};

const DEFAULT_OPTIONS: OptionsType = {
  message: DEFAULT_MESSAGE,
};

export default function useList<
  ItemType extends object,
  FilterOption extends Record<string, unknown>
>(
  listRequestFn:
    | ((params: any) => Promise<any>)
    | ((params: any, options?: { signal?: AbortSignal }) => Promise<any>),
  filterOption: Ref<FilterOption>,
  options: OptionsType = DEFAULT_OPTIONS
) {
  const {
    immediate = true,
    preRequest,
    message = DEFAULT_MESSAGE,
    defaultPageSize = 15,
    pagination = true,
  } = options;
  const { GET_DATA_IF_FAILED } = message;
  // 加载态
  const loading = ref(false);
  // 当前页
  const curPage = ref(1);
  // 总
  const total = ref(0);
  // 分页大小
  const pageSize = ref(defaultPageSize);
  // 数据
  const list = ref<ItemType[]>([]);
  // 原始返回数据
  const rawData = ref<any>();
  // 获取数据
  const loadData = async (page?: number, signal?: AbortSignal) => {
    const currentPage = page ?? curPage.value;
    if (typeof currentPage === 'number') curPage.value = currentPage;
    loading.value = true;
    try {
      preRequest?.();
      const params = {
        ...filterOption.value,
      } as Record<string, any>;

      // 处理分页参数
      if (pagination) {
        // 如果 params 中有 pagination 对象，更新它
        if (params.pagination && typeof params.pagination === 'object') {
          params.pagination = {
            ...params.pagination,
            page: currentPage,
            page_size: pageSize.value,
          };
        } else {
          // 否则直接添加 page 和 page_size
          params.page = currentPage;
          params.page_size = pageSize.value;
        }
      }

      // 检查函数参数数量，兼容只接受一个参数的函数
      const res =
        listRequestFn.length > 1
          ? await (
              listRequestFn as (
                params: any,
                options?: { signal?: AbortSignal }
              ) => Promise<any>
            )(params, { signal })
          : await (listRequestFn as (params: any) => Promise<any>)(params);

      // 支持多种响应格式：result/count（旧）、list/total（新）、results/total_count
      list.value = res.data.result || res.data.list || res.data.results || [];
      total.value =
        res.data.count ?? res.data.total ?? res.data.total_count ?? 0;
      rawData.value = res;
      options?.requestSuccess?.();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      if (GET_DATA_IF_FAILED) {
        Message.error(GET_DATA_IF_FAILED);
      }
      options?.requestError?.();
    } finally {
      loading.value = false;
    }
  };
  // 过滤数据
  const reload = () => {
    loadData();
  };
  // 重置数据
  const reset = (excludeKeys: string[] = []) => {
    if (!filterOption.value) return;
    const keys = Reflect.ownKeys(filterOption.value);
    // filterOption.value = {} as FilterOption;
    keys
      .filter((key) => !excludeKeys.includes(key as string))
      .forEach((key) => {
        Reflect.set(filterOption.value, key, undefined);
      });
    loadData(1);
  };

  const filter = () => {
    loadData();
  };

  // 监听分页数据改变
  watch([curPage, pageSize], () => {
    loadData(curPage.value);
  });

  onMounted(() => {
    if (immediate) {
      loadData(curPage.value);
    }
  });

  return {
    loading,
    curPage,
    total,
    list,
    filterOption,
    reload,
    reset,
    filter,
    pageSize,
    loadData,
    rawData,
  };
}
